<template>
    <template v-if="!$ff('group-profile-admin-buttons-together')">
        <VJoyDrawerTrigger :drawer="drawer">
            <VJoyButton
                v-if="isAdmin"
                size="small"
                variant="admin"
                class="profile-wrapper__vetting-panel-button"
                data-testid="vetting-panel-open-button"
                @click="onVettingPanelButtonClick"
            >
                {{ t('profile.vetting.drawer.button') }}
            </VJoyButton>
        </VJoyDrawerTrigger>
    </template>

    <VJoyDrawer
        id="vettingDrawer"
        ref="drawer"
        class="vettingDrawer profile-edition-vetting-drawer"
        :elevated="true"
        size="large"
        @before-hide="handleDrawerBeforeHide"
        @drawer:hide="handleDrawerHide"
        @drawer:open="handleDrawerOpen"
    >
        <template #drawer-body>
            <section v-if="pending">
                <VJoyWrapper v-bind="{align: 'center'}" justify="center" no-margin class="joy-mt-4">
                    <VJoySpinner />
                    <p>
                        {{ t('profile.vetting.drawer.loading') }}
                    </p>
                </VJoyWrapper>
            </section>
            <p v-else-if="error">An error occurred while fetching the data, try refreshing the page. Error: {{ error }}.</p>
            <NuxtErrorBoundary v-else @error="logError">
                <VJoyTabs v-bind="{selectedTab: 'qualification'}">
                    <VJoyTab tab="qualification" :label="t('profile.vetting.drawer.tab.qualification')">
                        <div class="scrollable-tab"><Qualification /></div>
                    </VJoyTab>
                    <VJoyTab tab="notes" :label="t('profile.vetting.drawer.tab.profile.notes')">
                        <NotesWidgets :profile="profile" />
                    </VJoyTab>
                </VJoyTabs>
                <template #error="{error: errorBoundary}">
                    <p>An error occurred: {{ errorBoundary }}</p>
                </template>
            </NuxtErrorBoundary>
        </template>
    </VJoyDrawer>
    <ConfirmVettingChangeDialog />
</template>

<script setup lang="ts">
    import {onMounted, onUnmounted, ref, toRef} from 'vue';
    import {navbarEventBus} from '@navbar/public-api';
    import {storeToRefs} from 'pinia';
    import {useHasEnoughPriviledge, useLazyAsyncData, useSplitScreenFromDrawer, useTranslation} from '#imports';
    import {VJoyButton, VJoyDrawer, VJoyDrawerTrigger, VJoySpinner, VJoyTab, VJoyTabs, VJoyWrapper} from '@maltjoy/core-vue';
    import ConfirmVettingChangeDialog from '~/components/admin-section-tabs/community/ConfirmVettingChangeDialog.vue';
    import NotesWidgets from '~/components/admin-section-tabs/notes/ui/NotesWidgets.vue';
    import Qualification from '~/components/admin-section-tabs/qualification/Qualification.vue';
    import {eventBus} from '~/config/events';
    import {useFreelancerVerticalStore} from '~/store/freelancerVerticalStore';
    import {useProfileQualificationStore} from '~/store/profileQualificationStore';
    import type {Profile} from '~/types/domain/Profile';

    const {t} = useTranslation();
    const isAdmin = await useHasEnoughPriviledge(['ROLE_ADMIN']);

    const props = defineProps<{
        profile: Profile;
        /**
         * used by useSplitScreenFromDrawer. It's the DOM element ref that we want to make still scrollable when drawer is open.
         */
        wrapperRef: HTMLElement;
    }>();

    const emits = defineEmits<{
        (e: 'vettingPanel:open'): void;
        (e: 'vettingPanel:close'): void;
    }>();

    const drawer = ref<InstanceType<typeof VJoyDrawer> | null>(null);
    const wrapperRef = toRef(props, 'wrapperRef'); // wrapperRef loses its reactivity when passed as a property

    const {addResizeEventListener, computeRemainingSpaceBetweenNavbarAndDrawer, removeResizeEventListener, resetSplitScreenBehavior} =
        useSplitScreenFromDrawer({areaToMakeScrollable: wrapperRef});
    const freelancerVerticalStore = useFreelancerVerticalStore();
    const profileQualificationStore = useProfileQualificationStore();
    const {qualification} = storeToRefs(profileQualificationStore);

    const {pending, error} = useLazyAsyncData(
        async () => {
            const qualificationState = await profileQualificationStore.init(props.profile.profileId);
            const verticalState = await freelancerVerticalStore.init(
                props.profile.profileId,
                true,
                qualification.value.emailAddress,
                props.profile.firstname,
                props.profile.lastname,
            );

            return {qualificationState, verticalState};
        },
        {deep: true},
    );

    function handleDrawerBeforeHide() {
        if (profileQualificationStore.inEditMode && !window.confirm(t('profile.vetting.close.drawer.dialog'))) {
            return false;
        }

        resetSplitScreenBehavior();

        return true;
    }

    function handleDrawerHide() {
        profileQualificationStore.leaveEditMode();
        removeResizeEventListener();
        emits('vettingPanel:close');
    }

    function handleDrawerOpen() {
        computeRemainingSpaceBetweenNavbarAndDrawer();
        addResizeEventListener();
    }

    function logError(err: any) {
        throw err;
    }

    function showPopup() {
        if (profileQualificationStore.inEditMode) {
            return 'something in order to show the popup';
        }
    }

    function onVettingPanelButtonClick() {
        /**
         * Notify to the parent element that the panel is open,
         * in order to apply the specific HTML class to override the style and push the content left
         */
        emits('vettingPanel:open');
        /**
         * Notify the navbar that we want to save space and close it.
         * nuxt-navbar-module handles this event and automatically reduce the navbar to its minimum width
         */
        navbarEventBus.emit('request:navbar-desktop-reduced');
        /**
         * Finally, computed the space between navbar and drawer,
         * to create an horizontal scrollable area for the background content
         */
        props.wrapperRef.classList.add('profile-wrapper--reduced');
    }

    function openDrawer() {
        drawer.value?.show();
        onVettingPanelButtonClick();
    }

    onMounted(() => {
        window.addEventListener('beforeunload', showPopup);
        eventBus.on('vetting:edit-mode', openDrawer);
    });

    onUnmounted(() => {
        window.removeEventListener('beforeunload', showPopup);
        eventBus.off('vetting:edit-mode', openDrawer);
    });
</script>

<style lang="scss" scoped>
    :deep(h4) {
        margin-bottom: 6px;
    }

    .joy-tab__selected {
        margin-top: 0;
    }
    .profile-wrapper__vetting-panel-button {
        position: fixed;
        top: calc(var(--header-height) + var(--joy-core-spacing-12));
        right: var(--joy-core-spacing-5);
        z-index: 10;
    }
    .scrollable-tab {
        height: calc(100vh - 200px);
        overflow-y: scroll;
    }
</style>
<style lang="scss">
    .profile-edition-vetting-drawer {
        --drawer-width: 650px;
        // Dialog and drawers are using the same z-index,
        // here we want the dialog to be visible when the drawer is opened
        z-index: calc(var(--joy-core-z-index-dialog) - 2) !important;
    }
</style>
